import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { SigilMark } from "../components/Sigil";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-ink/60 border border-gold/25 rounded-lg px-4 py-3 text-sm text-parchment placeholder:text-parchment-muted/50 focus:outline-none focus:border-gold/70 transition-colors";

const DeliveryForm = ({ orderId, packageName }) => {
  const [method, setMethod] = useState("email");
  const [form, setForm] = useState({ email: "", full_name: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/orders/${orderId}/delivery`, {
        method,
        email: method === "email" ? form.email : null,
        full_name: method === "post" ? form.full_name : null,
        line1: method === "post" ? form.line1 : null,
        line2: method === "post" ? form.line2 || null : null,
        city: method === "post" ? form.city : null,
        state: method === "post" ? form.state : null,
        pincode: method === "post" ? form.pincode : null,
        country: method === "post" ? form.country || "India" : null,
      });
      setDone(true);
      toast.success("Your destination is sealed in the ledger.");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not record the destination. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="delivery-confirmed" className="mt-10 parchment-card rounded-2xl p-8 max-w-lg mx-auto text-center">
        <p className="font-serif text-xl text-gold mb-2">The destination is sealed</p>
        <p className="text-sm text-parchment-muted">
          {method === "email"
            ? "Your written prediction will fly to your inbox within three days."
            : "Your written prediction will be sealed in an envelope and sent by post."}
        </p>
      </div>
    );
  }

  return (
    <motion.form
      data-testid="delivery-form"
      onSubmit={submit}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.7 }}
      className="mt-10 parchment-card rounded-2xl p-8 max-w-lg mx-auto text-left"
    >
      <p className="text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2">One last step</p>
      <h2 className="font-serif text-xl text-parchment mb-1">Where shall your prediction travel?</h2>
      <p className="text-xs text-parchment-muted mb-6">{packageName || "Your working"} — choose post or email, as you wish.</p>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {[
          { v: "email", label: "By Email", icon: Mail },
          { v: "post", label: "By Post", icon: Send },
        ].map((o) => (
          <button
            key={o.v}
            type="button"
            data-testid={`delivery-method-${o.v}`}
            onClick={() => setMethod(o.v)}
            className={`flex items-center justify-center gap-2 rounded-lg border py-3.5 text-xs uppercase tracking-[0.15em] transition-colors ${
              method === o.v ? "border-gold bg-gold/10 text-gold" : "border-gold/20 text-parchment-muted hover:border-gold/50"
            }`}
          >
            <o.icon size={15} /> {o.label}
          </button>
        ))}
      </div>

      {method === "email" ? (
        <div>
          <label htmlFor="delivery-email" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2">Email Address</label>
          <input id="delivery-email" data-testid="delivery-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="delivery-name" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2">Full Name</label>
            <input id="delivery-name" data-testid="delivery-name-input" required value={form.full_name} onChange={set("full_name")} placeholder="Receiver's name" className={inputCls} />
          </div>
          <div>
            <label htmlFor="delivery-line1" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2">Address Line 1</label>
            <input id="delivery-line1" data-testid="delivery-line1-input" required value={form.line1} onChange={set("line1")} placeholder="House / street" className={inputCls} />
          </div>
          <div>
            <label htmlFor="delivery-line2" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2">Address Line 2 <span className="text-parchment-muted/60">(optional)</span></label>
            <input id="delivery-line2" data-testid="delivery-line2-input" value={form.line2} onChange={set("line2")} placeholder="Area / landmark" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="delivery-city" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2">City</label>
              <input id="delivery-city" data-testid="delivery-city-input" required value={form.city} onChange={set("city")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="delivery-state" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2">State</label>
              <input id="delivery-state" data-testid="delivery-state-input" required value={form.state} onChange={set("state")} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="delivery-pincode" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2">Pincode</label>
              <input id="delivery-pincode" data-testid="delivery-pincode-input" required value={form.pincode} onChange={set("pincode")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="delivery-country" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2">Country</label>
              <input id="delivery-country" data-testid="delivery-country-input" required value={form.country} onChange={set("country")} className={inputCls} />
            </div>
          </div>
        </div>
      )}

      <button
        data-testid="delivery-submit-button"
        type="submit"
        disabled={sending}
        className="mt-7 w-full bg-gold text-ink rounded-full py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-amber transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {sending ? (<><Loader2 size={15} className="animate-spin" /> Sealing…</>) : "Seal the Destination"}
      </button>
    </motion.form>
  );
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setStatus("unknown");
      return;
    }
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${orderId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clearInterval(poll);
        } else if (attempts >= 15) {
          setStatus("pending");
          clearInterval(poll);
        }
      } catch {
        if (attempts >= 15) {
          setStatus("unknown");
          clearInterval(poll);
        }
      }
    }, 2000);
    return () => clearInterval(poll);
  }, [orderId]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen bg-ink flex flex-col items-center justify-center px-6 py-16 text-center"
    >
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className={status === "paid" ? "text-gold" : "text-gold/40"}
      >
        <SigilMark className="w-28 h-28" spin />
      </motion.div>

      {status === "checking" && (
        <>
          <h1 className="font-serif text-3xl text-parchment mt-10 flex items-center gap-3">
            <Loader2 className="animate-spin text-gold" size={22} /> Consulting the ledger…
          </h1>
          <p className="text-parchment-muted mt-4 text-sm">Confirming your tribute with Razorpay.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <h1 data-testid="payment-success-heading" className="font-display text-3xl sm:text-4xl text-gold text-glow-gold mt-10">
            The Working Has Begun
          </h1>
          <p className="text-parchment-muted mt-5 max-w-md text-sm leading-relaxed mx-auto">
            Your payment is confirmed. The practitioner will make contact within one
            lunar day to commence your rite.
          </p>
          {order && !order.delivery_recorded && (
            <DeliveryForm orderId={orderId} packageName={order.package_name} />
          )}
          {order?.delivery_recorded && (
            <p data-testid="delivery-confirmed" className="mt-8 text-sm text-gold font-mono tracking-wider">
              Your destination is sealed in the ledger.
            </p>
          )}
        </>
      )}
      {(status === "pending" || status === "unknown") && (
        <>
          <h1 className="font-serif text-3xl text-parchment mt-10">The stars are aligning…</h1>
          <p className="text-parchment-muted mt-4 max-w-md text-sm leading-relaxed">
            Confirmation is taking longer than usual. If your account was charged, your
            working is secured — otherwise no tribute was taken.
          </p>
        </>
      )}

      <Link
        to="/"
        data-testid="payment-success-home-link"
        className="mt-12 border border-gold/60 text-gold text-xs uppercase tracking-[0.2em] rounded-full px-8 py-4 hover:bg-gold/10 transition-colors"
      >
        Return to the Sanctum
      </Link>
    </div>
  );
}
